import { useState, useCallback, useMemo } from 'react';
import { Product } from '../../../types/product';
import {
  ValidationState,
  ValidationErrors,
  UseProductFormValidationReturn,
} from './ProductFormTypes';

/**
 * Product Form Validation Hook
 * Centralized validation logic for product form fields
 */

// =============================================================================
// Validation Rules
// =============================================================================

const validationRules = {
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: /^[a-zA-ZçğıöşüÇĞIİÖŞÜ0-9\s\-\.\,]+$/,
    message: 'Ürün adı gereklidir (2-100 karakter, özel karakterler hariç)',
  },
  description: {
    required: true,
    minLength: 10,
    maxLength: 500,
    message: 'Ürün açıklaması gereklidir (10-500 karakter)',
  },
  price: {
    required: true,
    min: 0.01,
    max: 99999,
    message: 'Geçerli bir fiyat giriniz (0.01 - 99,999 ₺)',
  },
  image_url: {
    required: false,
    pattern: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
    message: 'Geçerli bir URL formatı giriniz',
  },
  main_category_id: {
    required: true,
    message: 'Ana kategori seçimi zorunludur',
  },
  category_id: {
    required: true,
    message: 'Alt kategori seçimi zorunludur',
  },
};

// =============================================================================
// Validation Hook
// =============================================================================

export const useProductFormValidation = (
  product: Partial<Product>
): UseProductFormValidationReturn => {
  const [errors, setErrors] = useState<ValidationErrors>({});

  // =============================================================================
  // Field Validation Functions
  // =============================================================================

  const validateName = useCallback((name?: string): boolean => {
    const rules = validationRules.name;

    if (!name || !name.trim()) {
      if (rules.required) {
        setErrors(prev => ({ ...prev, name: 'Ürün adı zorunludur' }));
        return false;
      }
      return true;
    }

    const trimmedName = name.trim();

    if (trimmedName.length < rules.minLength) {
      setErrors(prev => ({
        ...prev,
        name: `Ürün adı en az ${rules.minLength} karakter olmalıdır`,
      }));
      return false;
    }

    if (trimmedName.length > rules.maxLength) {
      setErrors(prev => ({
        ...prev,
        name: `Ürün adı en fazla ${rules.maxLength} karakter olmalıdır`,
      }));
      return false;
    }

    if (!rules.pattern.test(trimmedName)) {
      setErrors(prev => ({ ...prev, name: 'Ürün adında geçersiz karakterler bulunuyor' }));
      return false;
    }

    setErrors(prev => ({ ...prev, name: undefined }));
    return true;
  }, []);

  const validateDescription = useCallback((description?: string): boolean => {
    const rules = validationRules.description;

    if (!description || !description.trim()) {
      if (rules.required) {
        setErrors(prev => ({ ...prev, description: 'Ürün açıklaması zorunludur' }));
        return false;
      }
      return true;
    }

    const trimmedDescription = description.trim();

    if (trimmedDescription.length < rules.minLength) {
      setErrors(prev => ({
        ...prev,
        description: `Açıklama en az ${rules.minLength} karakter olmalıdır`,
      }));
      return false;
    }

    if (trimmedDescription.length > rules.maxLength) {
      setErrors(prev => ({
        ...prev,
        description: `Açıklama en fazla ${rules.maxLength} karakter olmalıdır`,
      }));
      return false;
    }

    setErrors(prev => ({ ...prev, description: undefined }));
    return true;
  }, []);

  const validatePrice = useCallback((price?: number): boolean => {
    const rules = validationRules.price;

    if (price === undefined || price === null) {
      if (rules.required) {
        setErrors(prev => ({ ...prev, price: 'Fiyat zorunludur' }));
        return false;
      }
      return true;
    }

    if (price < rules.min) {
      setErrors(prev => ({ ...prev, price: `Fiyat en az ${rules.min} ₺ olmalıdır` }));
      return false;
    }

    if (price > rules.max) {
      setErrors(prev => ({ ...prev, price: `Fiyat en fazla ${rules.max} ₺ olmalıdır` }));
      return false;
    }

    setErrors(prev => ({ ...prev, price: undefined }));
    return true;
  }, []);

  const validateCategories = useCallback(
    (mainCategoryId?: string, categoryId?: string): boolean => {
      let isValid = true;
      let errorMessage = '';

      if (!mainCategoryId) {
        errorMessage = 'Ana kategori seçimi zorunludur';
        isValid = false;
      } else if (!categoryId) {
        errorMessage = 'Alt kategori seçimi zorunludur';
        isValid = false;
      }

      if (!isValid) {
        setErrors(prev => ({ ...prev, categories: errorMessage }));
      } else {
        setErrors(prev => ({ ...prev, categories: undefined }));
      }

      return isValid;
    },
    []
  );

  const validateImageUrl = useCallback((imageUrl?: string): boolean => {
    const rules = validationRules.image_url;

    if (!imageUrl || !imageUrl.trim()) {
      // Image URL is optional
      setErrors(prev => ({ ...prev, image_url: undefined }));
      return true;
    }

    if (!rules.pattern.test(imageUrl.trim())) {
      setErrors(prev => ({ ...prev, image_url: 'Geçerli bir URL formatı giriniz' }));
      return false;
    }

    setErrors(prev => ({ ...prev, image_url: undefined }));
    return true;
  }, []);

  // =============================================================================
  // Generic Field Validation
  // =============================================================================

  const validateField = useCallback(
    (field: keyof Product): boolean => {
      switch (field) {
        case 'name':
          return validateName(product.name);
        case 'description':
          return validateDescription(product.description);
        case 'price':
          return validatePrice(product.price);
        case 'image_url':
          return validateImageUrl(product.image_url);
        case 'main_category_id':
        case 'category_id':
          return validateCategories(product.main_category_id, product.category_id);
        default:
          return true;
      }
    },
    [
      product,
      validateName,
      validateDescription,
      validatePrice,
      validateCategories,
      validateImageUrl,
    ]
  );

  // =============================================================================
  // Form-wide Validation
  // =============================================================================

  const validateAll = useCallback((): boolean => {
    const nameValid = validateName(product.name);
    const descriptionValid = validateDescription(product.description);
    const priceValid = validatePrice(product.price);
    const categoriesValid = validateCategories(product.main_category_id, product.category_id);
    const imageUrlValid = validateImageUrl(product.image_url);

    return nameValid && descriptionValid && priceValid && categoriesValid && imageUrlValid;
  }, [
    product,
    validateName,
    validateDescription,
    validatePrice,
    validateCategories,
    validateImageUrl,
  ]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // =============================================================================
  // Computed Values
  // =============================================================================

  const validation = useMemo(
    (): ValidationState => ({
      isNameValid: validateName(product.name),
      isDescriptionValid: validateDescription(product.description),
      isPriceValid: validatePrice(product.price),
      isCategoryValid: validateCategories(product.main_category_id, product.category_id),
    }),
    [product, validateName, validateDescription, validatePrice, validateCategories]
  );

  const hasErrors = useMemo(() => {
    return Object.values(errors).some(error => error !== undefined);
  }, [errors]);

  return {
    validation,
    errors,
    validateAll,
    validateField,
    clearErrors,
    hasErrors,
  };
};

// =============================================================================
// Validation Utilities
// =============================================================================

/**
 * Get validation class name for input fields
 * @param isValid - Whether the field is valid
 * @param hasValue - Whether the field has a value (to show validation state)
 * @returns CSS class string
 */
export const getValidationClassName = (isValid: boolean, hasValue: boolean): string => {
  if (!hasValue) {
    return '';
  }
  return isValid
    ? 'border-green-300 focus:border-green-500'
    : 'border-red-300 focus:border-red-500';
};

/**
 * Get error message component props
 * @param error - Error message
 * @returns Error component props or null
 */
export const getErrorProps = (error?: string) => {
  if (!error) {
    return null;
  }

  return {
    className: 'text-red-500 text-xs mt-1 flex items-center gap-1',
    children: error,
  };
};
